import {
  AutoOrderEmailStatusType,
  AutoOrderIQStatusType,
  AutoOrderPDFStatusType,
  AutoOrderScheduledType,
  AutoOrderStatusType,
} from './auto-order-order-status-types.enum';
import {FieldValue} from '@angular/fire/firestore';
import {AutoOrderOrderErrorEnum, AutoOrderOrderItemErrorEnum} from './auto-order-order-error.enum';

//#region TYPE AutoOrderItemNew
/**
 * ### AutoOrderItemNew
 * Union type representing the status or state of an auto order item.
 *
 * Possible values:
 * - `boolean` — Indicates a default or active state.
 * - `'PERIOD-2'` — Represents a specific period state (e.g., period 2).
 * - `'ADDED'` — Indicates the item was newly added.
 *
 * @typedef {'PERIOD-2' | 'ADDED' | boolean} AutoOrderItemNew
 */
export type AutoOrderItemNew = boolean | 'PERIOD-2' | 'ADDED';
//#endregion

//#region INTERFACE IAutoOrderFirestoreOrder
/**
 * ### IAutoOrderFirestoreOrder
 * Represents a Firestore stored auto order with all relevant metadata, status, and order details.
 *
 * @interface IAutoOrderFirestoreOrder
 * @extends Object
 * @description This interface defines the structure of an auto order stored in Firestore,
 *              including status flags, error info, order details, and scheduling metadata.
 *
 * @example
 * const exampleOrder: IAutoOrderFirestoreOrder = {
 *   emailStatus: 'SENT',
 *   errorMsg: null,
 *   generated: new Date(),
 *   iqStatus: 'SUCCESS',
 *   orderId: 'ORDER123',
 *   orderItems: [{ code: 'ABC123', netSold: 12 }],
 *   orderSettings: { growth: 5, monthsHistory: 3, period: 14, stockDays: 7 },
 *   owned: 'org_456',
 *   pdfStatus: 'READY',
 *   scheduled: 'SCHEDULED',
 *   status: 'CREATED',
 *   supplierId: 'SUP789',
 *   userId: 'USER123',
 *   executed: null,
 *   noRecipients: false,
 *   preparedInfo: { calculationLog: [], notes: 'Prepared manually' },
 *   purchaseOrderNo: 'PO456'
 * };
 */
export interface IAutoOrderFirestoreOrder extends Object {
  /**
   * Status indicating the result of the email process for the order.
   *
   * @type {AutoOrderEmailStatusType}
   * @example 'SENT'
   */
  emailStatus: AutoOrderEmailStatusType;

  errorMsg: string | null | {code: string; message: string;};

  /**
   * The date and time when the order was generated.
   *
   * @type {Date | null}
   * @default null
   * @example new Date()
   */
  generated: Date | null;

  /**
   * Status indicating the result of internal queue processing.
   *
   * @type {AutoOrderIQStatusType}
   * @example 'SUCCESS'
   */
  iqStatus: AutoOrderIQStatusType;

  /**
   * Unique identifier assigned to the order.
   *
   * @type {string}
   * @example 'ORDER123'
   */
  orderId: string;

  /**
   * A list of order items included in this order.
   *
   * @type {IAutoOrderFirestoreOrderItem[]}
   * @example [ { code: 'ABC123', netSold: 12 } ]
   */
  orderItems: IAutoOrderFirestoreOrderItem[];

  /**
   * The configuration and parameters used to generate this order.
   *
   * @type {IAutoOrderFirestoreOrderSettings}
   * @example { growth: 5, monthsHistory: 3, period: 14, stockDays: 7 }
   */
  orderSettings: IAutoOrderFirestoreOrderSettings;

  /**
   * Identifier for the owning organization or context.
   *
   * @type {string}
   * @example 'org_456'
   */
  owned: string;

  /**
   * Status indicating whether the order PDF was generated successfully.
   *
   * @type {AutoOrderPDFStatusType}
   * @example 'READY'
   */
  pdfStatus: AutoOrderPDFStatusType;

  /**
   * Scheduling category of the order (e.g., scheduled, unscheduled).
   *
   * @type {AutoOrderScheduledType}
   * @example 'SCHEDULED'
   */
  scheduled: AutoOrderScheduledType;

  /**
   * Main lifecycle status of the order.
   *
   * @type {AutoOrderStatusType}
   * @example 'CREATED'
   */
  status: AutoOrderStatusType;

  /**
   * Identifier of the supplier related to this order.
   *
   * @type {string}
   * @example 'SUP789'
   */
  supplierId: string;

  /**
   * Identifier of the user who created or owns the order.
   *
   * @type {string}
   * @example 'USER123'
   */
  userId: string;

  error?: IAutoOrderFirestoreOrderErrorMessage | null;

  /**
   * Timestamp marking when the order was executed.
   *
   * @type {Date | null}
   * @optional
   * @example new Date()
   */
  executed?: Date | null;

  /**
   * Per-item errors when IQ submission or pre-submit validation fails.
   *
   * @type {Record<string, { desc: string; error: number; reason?: string }> | null}
   * @optional
   */
  itemErrors?: Record<string, { desc: string; error: number; reason?: string }> | null;

  /**
   * Indicates whether there were no recipients for the order communication.
   *
   * @type {boolean}
   * @optional
   * @example false
   */
  noRecipients?: boolean;

  /**
   * Additional preparation data collected during order setup.
   *
   * @type {IAutoOrderFirestoreOrderPreparedInfo}
   * @optional
   * @example { calculationLog: [], notes: 'Prepared manually' }
   */
  preparedInfo?: IAutoOrderFirestoreOrderPreparedInfo;

  /**
   * Optional override or external purchase order number.
   *
   * @type {string}
   * @optional
   * @example 'PO456'
   */
  purchaseOrderNo?: string;
}

//#endregion

//#region INTERFACE IAutoOrderFirestoreOrderSettings

/**
 * ### IAutoOrderFirestoreOrderSettings
 * Configuration settings used for generating auto orders.
 *
 * @interface IAutoOrderFirestoreOrderSettings
 * @extends Object
 * @description Defines the key parameters controlling auto order calculations such as growth rate,
 *              history length, ordering period, and stock days.
 *
 * @example
 * const example: IAutoOrderFirestoreOrderSettings = {
 *   growth: 5,
 *   monthsHistory: 3,
 *   period: 14,
 *   stockDays: 7
 * };
 */
export interface IAutoOrderFirestoreOrderSettings extends Object {
  /**
   * Growth percentage used in forecasting future order quantities.
   * @type {number}
   * @example 5
   */
  growth: number;

  /**
   * Number of months of historical data to consider for order calculations.
   * @type {number}
   * @example 3
   */
  monthsHistory: number;

  /**
   * Ordering period in days.
   * @type {number}
   * @example 14
   */
  period: number;

  /**
   * Number of stock days to maintain.
   * @type {number}
   * @example 7
   */
  stockDays: number;
}

//#endregion

//#region INTERFACE IAutoOrderFirestoreOrderItem

/**
 * ### IAutoOrderFirestoreOrderItem
 * Represents a single item within an auto order, including sales, stock, and supplier information.
 *
 * @interface IAutoOrderFirestoreOrderItem
 * @description Contains detailed metrics and flags for an individual item involved in auto order processing.
 *              These metrics are used to calculate order quantities, track stock levels, and manage supplier relations.
 *
 * @example
 * const example: IAutoOrderFirestoreOrderItem = {
 *   code: "ITEM123",
 *   mainSupplier: 1,
 *   maxReturned: 5,
 *   maxSold: 100,
 *   negativeDays: 2,
 *   netSold: 50,
 *   new: true, || 'PERIOD-2' || 'ADDED'
 *   outOfStock: 0,
 *   qtyDiff: 3,
 *   zeroDays: 7
 * };
 */
export interface IAutoOrderFirestoreOrderItem {
  /**
   * Unique code identifying the item.
   * @type {string}
   * @optional
   * @example "ITEM123"
   */
  code?: string;

  /**
   * Identifier for the main supplier of the item.
   * @type {number}
   * @optional
   * @example 1
   */
  mainSupplier?: number;

  /**
   * Maximum quantity of the item that can be returned.
   * @type {number}
   * @optional
   * @example 5
   */
  maxReturned?: number;

  /**
   * Maximum quantity of the item sold within a specific timeframe.
   * @type {number}
   * @optional
   * @example 100
   */
  maxSold?: number;

  /**
   * Number of days the item had negative sales or returns.
   * @type {number}
   * @optional
   * @example 2
   */
  negativeDays?: number;

  /**
   * Net quantity sold for the item.
   * @type {number}
   * @optional
   * @example 50
   */
  netSold?: number;

  /**
   * Indicates if the item is newly added.
   * @type {boolean | string}
   * @optional
   * @example true
   */
  new?: AutoOrderItemNew;

  /**
   * Quantity of the item currently out of stock.
   * @type {number}
   * @optional
   * @example 0
   */
  outOfStock?: number;

  /**
   * Difference in quantity from expected or previous values.
   * @type {number}
   * @optional
   * @example 3
   */
  qtyDiff?: number;

  /**
   * Number of days the item had zero sales.
   * @type {number}
   * @optional
   * @example 7
   */
  zeroDays?: number;
}

//#endregion

//#region INTERFACE IAutoOrderFirestoreOrderErrorMessage

/**
 * ### IAutoOrderFirestoreOrderErrorMessage
 * Represents error details related to an auto order processing failure.
 *
 * @interface IAutoOrderFirestoreOrderErrorMessage
 * @extends Object
 * @description Contains optional error code and message that describe the failure cause during auto order operations.
 */
export interface IAutoOrderFirestoreOrderErrorMessage<TError = AutoOrderOrderErrorEnum> extends Object {
  desc: string;
  error: TError,
  itemErrors: {
    [itemCode: string]: IAutoOrderFirestoreOrderErrorMessage<AutoOrderOrderItemErrorEnum>;
  }
}

//#endregion

//#region INTERFACE IAutoOrderFirestoreOrderPreparedInfoDetails

/**
 * ### IAutoOrderFirestoreOrderPreparedInfoDetails
 * Detailed information about a prepared order item.
 *
 * @interface IAutoOrderFirestoreOrderPreparedInfoDetails
 * @extends Object
 * @description Represents quantity, optional description, and optional price of an individual prepared order item.
 *
 * @example
 * const example: IAutoOrderFirestoreOrderPreparedInfoDetails = {
 *   qty: 5,
 *   desc: "Sample item description",
 *   price: 12.99
 * };
 */
export interface IAutoOrderFirestoreOrderPreparedInfoDetails extends Object {
  /**
   * Quantity prepared for the item.
   * @type {number}
   * @example 5
   */
  qty: number;

  /**
   * Optional description of the prepared item.
   * @type {string}
   * @optional
   * @example "Sample item description"
   */
  desc?: string;

  /**
   * Optional price for the prepared item.
   * @type {number}
   * @optional
   * @example 12.99
   */
  price?: number;
}

//#endregion

//#region INTERFACE IAutoOrderFirestoreOrderPreparedInfo

/**
 * ### IAutoOrderFirestoreOrderPreparedInfo
 * Aggregated prepared information for auto orders.
 *
 * @interface IAutoOrderFirestoreOrderPreparedInfo
 * @extends Object
 * @description Contains a dictionary of prepared order item details keyed by item code,
 *              the total quantity of all items, a timestamp, and optional comment and dirty flags.
 *
 * @example
 * const example: IAutoOrderFirestoreOrderPreparedInfo = {
 *   preparedItems: {
 *     "ITEM001": { qty: 5, desc: "Sample item description", price: 12.99 }
 *   },
 *   total: 5,
 *   ts: new Date(),
 *   comment: "Manually prepared",
 *   dirty: { "ITEM001": true }
 * };
 */
export interface IAutoOrderFirestoreOrderPreparedInfo extends Object {
  /**
   * Mapping of item codes to their prepared info details.
   * @type {{ [code: string]: IAutoOrderFirestoreOrderPreparedInfoDetails }}
   * @example { "ITEM001": { qty: 5, desc: "Sample item description", price: 12.99 } }
   */
  preparedItems: {[code: string]: IAutoOrderFirestoreOrderPreparedInfoDetails};

  /**
   * Total quantity of all prepared items.
   * @type {number}
   * @example 5
   */
  total: number;

  /**
   * Timestamp of the prepared information record.
   * @type {Date | FieldValue}
   * @example new Date()
   */
  ts: Date | FieldValue;

  /**
   * Optional comment regarding the prepared information.
   * @type {string}
   * @optional
   * @example "Manually prepared"
   */
  comment?: string;

  /**
   * Optional dirty flags for prepared items indicating if update is required.
   * @type {{ [code: string]: boolean }}
   * @optional
   * @example { "ITEM001": true }
   */
  dirty?: {[code: string]: boolean};

  /**
   * Origin of the prepared items, for example scanner-created orders.
   */
  source?: string;
}

//#endregion
